import {
  CuttingStandardCutoutSlot,
  ScriptCheckState,
  StandardCutoutAvailablePlates,
  StandardCutoutFragmentParams,
} from 'cloud-shared-lib';
import { CuttingButt } from './cutting-butt.model';
import { CuttingCustomHole } from './cutting-custom-hole.model';
import { CuttingFixedFastenersSet } from './cutting-fixed-fasteners-set.model';
import { CuttingHandle } from './cutting-handle.model';
import { CuttingMaterial } from './cutting-material.model';
import { CuttingPanelBaseSlot } from './cutting-panel-base-slot.model';

export class CuttingPanelOutline<T extends CuttingPanelBaseSlot> {
  topLeft: OutlineAngle;
  topRight: OutlineAngle;
  bottomRight: OutlineAngle;
  bottomLeft: OutlineAngle;

  topSide: OutlineSide<T>;
  bottomSide: OutlineSide<T>;
  leftSide: OutlineSide<T>;
  rightSide: OutlineSide<T>;

  cutouts: Cutout[] = [];
  faceBanding: FaceBand[];
  millings: Milling[] = [];

  constructor(outline?: CuttingPanelOutline<T> | any) {
    outline = outline || {};

    this.faceBanding = outline.faceBanding || [];

    this.topSide =
      outline.topSide || ({ groove: undefined, customHoles: [], fastenersSets: [], handle: null } as OutlineSide<T>);
    this.bottomSide =
      outline.bottomSide || ({ groove: undefined, customHoles: [], fastenersSets: [], handle: null } as OutlineSide<T>);
    this.leftSide =
      outline.leftSide || ({ groove: undefined, customHoles: [], fastenersSets: [], handle: null } as OutlineSide<T>);
    this.rightSide =
      outline.rightSide || ({ groove: undefined, customHoles: [], fastenersSets: [], handle: null } as OutlineSide<T>);

    this.topLeft = outline.topLeft || {
      chamferLength: 0,
      rounding: 0,
      cutX: 0,
      cutY: 0,
      mode: AngleMode.None,
      roundingInside: 0,
      cutInsideX: 0,
      cutInsideY: 0,
      cutInsideR: 0,
      diagonalCut: false,
      butt: undefined,
      cutInsideWithAngle: { r: 0, x: 0, xA: 90, y: 0, yA: 90, x1: 0, y1: 0 },
      masonsMitreFemale: { allowance: 0, radius: 0, orientation: MasonsMitreOrientationEnum.AlongHeight, length: 100 },
      masonsMitreMale: { allowance: 0, radius: 0, orientation: MasonsMitreOrientationEnum.AlongWidth },
    };
    this.topRight = outline.topRight || {
      chamferLength: 0,
      rounding: 0,
      cutX: 0,
      cutY: 0,
      mode: AngleMode.None,
      roundingInside: 0,
      cutInsideX: 0,
      cutInsideY: 0,
      cutInsideR: 0,
      diagonalCut: false,
      butt: undefined,
      cutInsideWithAngle: { r: 0, x: 0, xA: 90, y: 0, yA: 90, x1: 0, y1: 0 },
      masonsMitreFemale: { allowance: 0, radius: 0, orientation: MasonsMitreOrientationEnum.AlongHeight, length: 100 },
      masonsMitreMale: { allowance: 0, radius: 0, orientation: MasonsMitreOrientationEnum.AlongWidth },
    };
    this.bottomRight = outline.bottomRight || {
      chamferLength: 0,
      rounding: 0,
      cutX: 0,
      cutY: 0,
      mode: AngleMode.None,
      roundingInside: 0,
      cutInsideX: 0,
      cutInsideY: 0,
      cutInsideR: 0,
      diagonalCut: false,
      butt: undefined,
      cutInsideWithAngle: { r: 0, x: 0, xA: 90, y: 0, yA: 90, x1: 0, y1: 0 },
      masonsMitreFemale: { allowance: 0, radius: 0, orientation: MasonsMitreOrientationEnum.AlongHeight, length: 100 },
      masonsMitreMale: { allowance: 0, radius: 0, orientation: MasonsMitreOrientationEnum.AlongWidth },
    };
    this.bottomLeft = outline.bottomLeft || {
      chamferLength: 0,
      rounding: 0,
      cutX: 0,
      cutY: 0,
      mode: AngleMode.None,
      roundingInside: 0,
      cutInsideX: 0,
      cutInsideY: 0,
      cutInsideR: 0,
      diagonalCut: false,
      butt: undefined,
      cutInsideWithAngle: { r: 0, x: 0, xA: 90, y: 0, yA: 90, x1: 0, y1: 0 },
      masonsMitreFemale: { allowance: 0, radius: 0, orientation: MasonsMitreOrientationEnum.AlongHeight, length: 100 },
      masonsMitreMale: { allowance: 0, radius: 0, orientation: MasonsMitreOrientationEnum.AlongWidth },
    };
  }
}

export class FaceBand {
  material: CuttingMaterial;
  level: number;
  order: number;
}

export class Milling {
  id: number;
  name: string;
  description: string;
  сheckState: ScriptCheckState;
  checkedRemark: string;
}

export interface OutlineAngle {
  rounding: number;
  roundingInside: number;
  cutInsideX: number;
  cutInsideY: number;
  cutInsideR: number;
  cutX: number;
  cutY: number;
  chamferLength: number;
  cutInsideWithAngle: CutInsightWithAngleParams;
  masonsMitreFemale: MasonsMitreFemaleParams;
  masonsMitreMale: MasonsMitreMaleParams;
  mode: AngleMode;
  butt: CuttingButt;
}

export class CutInsightWithAngleParams {
  x: number;
  y: number;
  xA: number;
  yA: number;
  x1: number;
  y1: number;
  r: number;
}

export class MasonsMitreMaleParams {
  allowance: number;
  radius: number;
  orientation: MasonsMitreOrientationEnum;
}

export class MasonsMitreFemaleParams extends MasonsMitreMaleParams {
  length: number;
}

export class Cutout {
  x: number;
  y: number;
  height: number;
  width: number;
  radius: number;
  type: CutoutTypeEnum;
  basePoint: CutoutPoint;
  angle: CutoutPoint;
  slotId?: number;
  slot?: CuttingStandardCutoutSlot;
  newPlate?: StandardCutoutAvailablePlates;
  slots: CuttingStandardCutoutSlot[];
  rotateDeg: number;
  fileName?: string;
  imageS3Key?: string;
  fragmentImageS3Key?: string;
  fragmentParams?: StandardCutoutFragmentParams;
}

export enum CutoutTypeEnum {
  Rectangle,
  Ellipse,
  Any,
  RoundedRectangle,
  Fragment,
}

export enum CutoutPoint {
  TopLeft,
  TopRight,
  BottomLeft,
  BottomRight,
  Center,
  Custom,
}

export enum AngleMode {
  None,
  Rounded,
  Cutted,
  RoundedInside,
  CuttedInside,
  DiagonalCut,
  Chamfer,
  MasonsMitreFemale,
  MasonsMitreMale,
  CuttedInsideWithAngle,
}

export enum PlateTypeEnum {
  Face,
  Back,
  Edge,
}

export enum MasonsMitreOrientationEnum {
  AlongHeight,
  AlongWidth,
}

export interface OutlineSide<S extends CuttingPanelBaseSlot> {
  groove: S;
  customHoles: CuttingCustomHole[];
  fastenersSets: CuttingFixedFastenersSet[];
  handle: CuttingHandle | null;
}

export type AnglePosition = 'tl' | 'tr' | 'br' | 'bl' | undefined;
